<style>
  /* bic-select zeigt bei onlyShowPlaceholder den Text standardmäßig grau (Placeholder-Optik) an.
     Wenn "Alle erlaubt" den tatsächlichen Auswahlwert darstellt, soll er wie ein normaler Wert wirken. */
  .force-value-color ::ng-deep input.text-gray-500 {
    color: #111827 !important;
  }

  /* bic-textarea unterstützt keine Höhenvererbung (nur feste rows). Damit Systemprompt- und
     Prüfprompt-Feld den verfügbaren Platz ausfüllen und sich dynamisch mit dem Fenster vergrößern,
     wird der Wrapper und das interne <textarea> hier auf 100% Höhe des flex-1 Elternelements gezwungen. */
  .prompt-textarea-fill {
    display: flex;
    flex: 1 1 auto;
    min-height: 120px;
  }
  .prompt-textarea-fill ::ng-deep div {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
  }
  .prompt-textarea-fill ::ng-deep textarea {
    flex: 1 1 auto;
    height: 100% !important;
    min-height: 0;
  }
</style>
<div class="flex flex-col h-full">
  <!-- Page Header -->
  <app-page-header>
    <div class="flex flex-row items-center gap-4 flex-1">
      <button (click)="navigateBack()" class="text-gray-500 hover:text-gray-700">
        <fa-icon [icon]="faChevronLeft"></fa-icon>
      </button>
      <span class="text-xl font-medium">{{ ruleset?.name }}</span>
      @if (readOnly) {
        @if (linkedFromOtherProject) {
          <span tooltip="Dieser myCheck wurde aus einem anderen Projekt geteilt und kann nur dort bearbeitet werden."
                tooltipDirection="bottom">
            <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">Geteilt · schreibgeschützt</bic-badge>
          </span>
        } @else {
          <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">Bereitgestellt · schreibgeschützt</bic-badge>
        }
      }
    </div>
    @if (readOnly) {
      <bic-button (onClick)="navigateBack()" buttonType="white">Schließen</bic-button>
    } @else if (quickstartActive) {
      <!-- Der Agent schreibt das myCheck gerade serverseitig – ein "Übernehmen" des Formulars würde
           seinen Stand überschreiben. Erst "Manuell bearbeiten" lädt neu und gibt frei. -->
      <bic-button (onClick)="leaveQuickstart()" buttonType="primary">Manuell bearbeiten</bic-button>
    } @else {
      @if (canRuleTest) {
        <!-- Testlauf (Figma „Testmodus" 01/06): deaktiviert bei erreichtem Tageslimit, der Tooltip
             hängt am Wrapper, weil ein deaktivierter Button keine Maus-Events bekommt. -->
        <span class="relative inline-flex" [tooltip]="ruleTestTooltip" [tooltipSubtext]="ruleTestTooltipSubtext"
              tooltipDirection="bottom">
          <bic-button (onClick)="openRuleTest()" [disabled]="ruleTestLimitReached" buttonType="white">
            <fa-icon iconLeft [icon]="faPlay"></fa-icon>
            Testlauf
          </bic-button>
          @if (ruleTestUnseen) {
            <span class="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green" aria-label="Neues Testergebnis"></span>
          }
        </span>
      }
      <bic-button (onClick)="navigateBack()" [disabled]="saving" buttonType="white">Abbrechen</bic-button>
      <bic-button (onClick)="isDirty ? save() : navigateBack()" [loading]="saving" buttonType="primary">
        <fa-icon iconLeft [icon]="isDirty ? faFloppyDisk : faChevronLeft"></fa-icon>
        {{ isDirty ? 'Speichern' : 'Zurück' }}
      </bic-button>
    }
  </app-page-header>

  <!-- Vorschlags-Banner: volle Seitenbreite direkt unter der Topbar, weil sich die Zusammenfassung
       auf alle Sektionen bezieht, nicht nur auf den aktuell ausgewählten Tab im rechten Panel. Steht die
       Review-Leiste im offenen Edit-Chat, übernimmt sie Zähler und „Alle übernehmen/verwerfen“. -->
  @if (pendingProposal && hasOpenProposalChanges && !proposalReview.isShownInChat()) {
    <div class="px-6 pt-6">
      <app-proposal-banner
        (applyClicked)="onProposalApplyClicked()"
        (discardClicked)="onProposalDiscardClicked()"
        [proposal]="pendingProposal">
      </app-proposal-banner>
    </div>
  }

  @if (loading) {
    <!-- Ladezustand: vorher rendete hier ein leeres Formular, während das Regelset noch lud. -->
    <div class="flex items-center justify-center flex-1 w-full gap-2">
      <fa-icon [icon]="faSpinnerThird" class="text-gray-500 animate-spin text-svg-16"></fa-icon>
    <span class="text-gray-400 text-sm">myCheck wird geladen…</span>
  </div>
  } @else {
    @if (quickstartActive) {
      <!-- Schutzhinweis: der Chat füllt dieses myCheck gerade. Das Formular zeigt noch den Stand vom
           Öffnen, deshalb ist "Übernehmen" hier gesperrt. -->
      <div class="mx-6 mt-6 flex flex-row items-start gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-4">
        <fa-icon [icon]="faSpinnerThird" class="mt-0.5 animate-spin text-yellow-600 text-svg-16"></fa-icon>
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-gray-900">myCheck Quickstart läuft</span>
          <span class="text-xs text-gray-600">
          chatBIC füllt dieses myCheck gerade im Chat. Dieses Formular zeigt noch den Stand vom
          Öffnen – „Manuell bearbeiten" lädt den aktuellen Stand und gibt das Speichern frei.
        </span>
        </div>
      </div>
    }
  <!-- Content: Left menu + Right panel -->
  <div class="flex flex-row flex-1 min-h-0 gap-6 p-6">
    <!-- Left sidebar menu: unterhalb Full HD (1920px) schmaler, Labels truncaten dann bei Bedarf
         statt die Leiste immer auf voller Breite zu halten (siehe tailwind.config.js `main-full-hd`). -->
      <div class="flex flex-col gap-4 w-[240px] main-full-hd:w-[300px] shrink-0 h-full min-h-full">
      <!-- Allgemeine Angaben -->
      <button (click)="selectSection('general')"
              class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left"
              [class]="selectedSection === 'general' ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
              [tooltip]="proposalNavTooltip(sectionNavStatus('general'))" tooltipColor="dark" tooltipDirection="right">
        <fa-icon [class]="selectedSection === 'general' ? 'text-base text-gray-900' : 'text-base text-gray-300 group-hover:text-gray-900'"
                 [icon]="faCircleInfo"></fa-icon>
        <span class="text-sm font-medium flex-1 truncate" [class]="selectedSection === 'general' ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-900'">Allgemeine Angaben</span>
        <app-proposal-nav-marker [status]="sectionNavStatus('general')"></app-proposal-nav-marker>
      </button>

      <!-- Regelparameter -->
      <button (click)="selectSection('parameters')"
              class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left"
              [class]="selectedSection === 'parameters' ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
              [tooltip]="proposalNavTooltip(sectionNavStatus('parameters'))" tooltipColor="dark" tooltipDirection="right">
        <fa-icon [class]="selectedSection === 'parameters' ? 'text-base text-gray-900' : 'text-base text-gray-300 group-hover:text-gray-900'"
                 [icon]="faGear"></fa-icon>
        <span class="text-sm font-medium flex-1 truncate" [class]="selectedSection === 'parameters' ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-900'">Regelparameter</span>
        <app-proposal-nav-marker [status]="sectionNavStatus('parameters')"></app-proposal-nav-marker>
      </button>

      <!-- Systemprompt -->
      <button (click)="selectSection('systemprompt')"
              class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left"
              [class]="selectedSection === 'systemprompt' ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
              [tooltip]="proposalNavTooltip(sectionNavStatus('systemprompt'))" tooltipColor="dark" tooltipDirection="right">
        <fa-icon [class]="selectedSection === 'systemprompt' ? 'text-base text-gray-900' : 'text-base text-gray-300 group-hover:text-gray-900'"
                 [icon]="faSparkles"></fa-icon>
        <span class="text-sm font-medium flex-1 truncate" [class]="selectedSection === 'systemprompt' ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-900'">Systemprompt</span>
        <app-proposal-nav-marker [status]="sectionNavStatus('systemprompt')"></app-proposal-nav-marker>
      </button>

      <!-- Ausgabeformat -->
      <button (click)="selectSection('output-format')"
              class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left"
              [class]="selectedSection === 'output-format' ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
              [tooltip]="proposalNavTooltip(sectionNavStatus('output-format'))" tooltipColor="dark" tooltipDirection="right">
        <fa-icon [class]="selectedSection === 'output-format' ? 'text-base text-gray-900' : 'text-base text-gray-300 group-hover:text-gray-900'"
                 [icon]="faFile"></fa-icon>
        <span class="text-sm font-medium flex-1 truncate" [class]="selectedSection === 'output-format' ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-900'">Ausgabeformat</span>
        <app-proposal-nav-marker [status]="sectionNavStatus('output-format')"></app-proposal-nav-marker>
      </button>

      <!-- Prüfregeln Header + Actions -->
      <div class="flex flex-row items-center gap-2">
        <span class="text-base font-medium text-gray-900 flex-1">Prüfregeln</span>
        <div tooltip="Prüfregeln aus Excel importieren" tooltipDirection="top">
          <bic-button (onClick)="openImportPopup()" buttonType="white">
            <fa-icon [icon]="faArrowUpFromBracket"></fa-icon>
          </bic-button>
        </div>
        <div class="relative" (click)="$event.stopPropagation()">
          <div tooltip="Filter" tooltipDirection="top">
            <bic-button (onClick)="toggleFilterPopup()" buttonType="white">
              <span class="inline-flex items-center gap-1">
                <fa-icon [class.text-green-500]="hasActiveFilters()" [icon]="faFilter" class="text-sm"></fa-icon>
                @if (activeFilterCount > 0) {
                  <span class="text-sm font-medium">({{ activeFilterCount }})</span>
                }
              </span>
            </bic-button>
          </div>
          @if (filterPopupVisible) {
            <div class="absolute right-0 top-10 z-30 w-[250px] bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col max-h-[450px]"
                 (click)="$event.stopPropagation()">
              <!-- Suchleiste -->
              <div class="px-3 pt-3 pb-2">
                <bic-input placeholder="Tag suchen..." [(value)]="filterSearch" [showX]="true"></bic-input>
              </div>

              <!-- Bereich 3: Tag-Werte mit Checkboxen -->
              <div class="flex-1 overflow-y-auto px-3 pb-3 flex flex-col gap-3">
                @if (filteredFilterTags.length === 0) {
                  <span class="text-xs text-gray-400 py-2">Keine passenden Tags gefunden.</span>
                } @else {
                  @for (tag of filteredFilterTags; track tag.id) {
                    <div class="flex flex-col gap-2">
                      <!-- Kategorie-Überschrift (dezente Trennung) -->
                      <div class="flex items-center gap-2 pt-1">
                        <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{{ tag.name }}</span>
                        <div class="flex-1 h-px bg-gray-100"></div>
                      </div>
                      <!-- Tag-Werte als Checkbox-Liste -->
                      @if (tag.options && tag.options.length > 0) {
                        @for (option of getFilteredOptions(tag); track option) {
                          <button class="flex items-center gap-2 px-2 py-1.5 rounded transition-colors text-left hover:bg-gray-50"
                                  (click)="toggleTagValueFilter(tag.id, option)">
                            <div class="w-4 h-4 rounded border flex items-center justify-center shrink-0"
                                 [class.bg-green-500]="isTagValueFilterActive(tag.id, option)"
                                 [class.border-green-500]="isTagValueFilterActive(tag.id, option)"
                                 [class.border-gray-300]="!isTagValueFilterActive(tag.id, option)">
                              @if (isTagValueFilterActive(tag.id, option)) {
                                <fa-icon [icon]="faCheck" class="text-white text-[10px]"></fa-icon>
                              }
                            </div>
                            <span class="text-sm text-gray-700">{{ option }}</span>
                          </button>
                        }
                      } @else {
                        <span class="text-xs text-gray-400 italic pl-6">Keine Optionen</span>
                      }
                    </div>
                  }
                }
              </div>

              <!-- Footer: Reset -->
              @if (hasActiveFilters()) {
                <div class="border-t border-gray-100 px-3 py-2">
                  <button class="text-xs text-red-500 hover:underline" (click)="clearTagFilters()">Filter zurücksetzen ({{ activeFilterCount }})</button>
                </div>
              }
            </div>
          }
        </div>
        <div [tooltip]="maxRulesTooltip" tooltipDirection="top">
          <bic-button (onClick)="addRule()" [disabled]="!canAddRule" buttonType="white">
            <fa-icon [icon]="faPlus"></fa-icon>
          </bic-button>
        </div>
      </div>

      @if (importPopupVisible) {
        <app-ki-rule-import-popup
          (closed)="closeImportPopup()"
          (downloadTemplate)="downloadRuleImportTemplate()"
          (fileSelected)="onImportFileSelected($event)"
          (replaceExistingChange)="importReplaceExisting = $event"
          [importing]="importingRules"
          [replaceExisting]="importReplaceExisting">
        </app-ki-rule-import-popup>
      }

      <!-- Rule list -->
      <div class="flex flex-col flex-1 min-h-0 w-full gap-2 overflow-y-auto overflow-x-visible">
        @for (rule of filteredRules; track getRuleKey(rule)) {
          <div class="relative">
            <button (click)="selectRule(rule)"
                    [class]="isRuleSelected(rule) ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
                    [class.bg-red-50]="showValidation && isRuleInvalid(rule) && !isRuleSelected(rule)"
                    [class.border-red-300]="showValidation && isRuleInvalid(rule)"
                    [tooltip]="proposalNavTooltip(ruleNavStatus(rule))" tooltipColor="dark" tooltipDirection="right"
                    class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left w-full">
              <fa-icon [class]="isRuleSelected(rule) ? 'text-base text-gray-900' : 'text-base text-gray-300 group-hover:text-gray-900'"
                       [icon]="faCube"></fa-icon>
              <span [class]="isRuleSelected(rule) ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-900'"
                    class="text-sm font-medium flex-1 truncate">{{ getRuleListLabel(rule) }}</span>
              @if (showValidation && isRuleInvalid(rule)) {
                <fa-icon [icon]="faCircleInfo" class="text-yellow-500 text-sm shrink-0"></fa-icon>
              }
              <!-- Punkt bei Vorschlags-Änderung; bei Hover (bzw. offenem Menü) steht dort das Menü. -->
              <app-proposal-nav-marker [hasMenu]="true" [menuOpen]="isRuleMenuOpen(rule)" [status]="ruleNavStatus(rule)">
                <span (click)="toggleRuleMenu($event, rule)"
                      class="flex items-center justify-center shrink-0 cursor-pointer">
                  <fa-icon [icon]="faEllipsisVertical" class="text-sm"></fa-icon>
                </span>
              </app-proposal-nav-marker>
            </button>
            @if (isRuleMenuOpen(rule)) {
              <div
                (click)="$event.stopPropagation()"
                class="absolute right-2 top-12 z-20 w-[160px] bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                <button (click)="duplicateRule(rule)"
                        class="flex flex-row items-center gap-2 w-full px-3 py-2 text-sm text-gray-900 hover:bg-gray-50">
                  <fa-icon [icon]="faCopy" class="text-xs"></fa-icon>
                  Duplizieren
                </button>
                <button (click)="deleteRule(rule)"
                        class="flex flex-row items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-gray-50">
                  <fa-icon [icon]="faTrash" class="text-xs"></fa-icon>
                  Löschen
                </button>
              </div>
            }
          </div>
        }
        <!-- Regeln, die der aktuelle Vorschlag entfernt – existieren nicht mehr in `rules`, daher
             separat von der normalen Auswahl-Liste. Ein Klick öffnet rechts die Karte mit
             „Löschen übernehmen"/„Behalten" (roter Punkt, Name durchgestrichen). -->
        @for (removedRule of removedProposalRules; track removedRule.id) {
          <button (click)="selectRemovedRule(removedRule)"
                  [class]="isRemovedRuleSelected(removedRule) ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
                  [tooltip]="proposalNavTooltip('removed')" tooltipColor="dark" tooltipDirection="right"
                  class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left w-full"
                  type="button">
            <fa-icon [class]="isRemovedRuleSelected(removedRule) ? 'text-base text-gray-900' : 'text-base text-gray-300 group-hover:text-gray-900'"
                     [icon]="faCube"></fa-icon>
            <span [class]="isRemovedRuleSelected(removedRule) ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-900'"
                  class="text-sm font-medium flex-1 truncate line-through">{{ getRemovedRuleListLabel(removedRule) }}</span>
            <app-proposal-nav-marker status="removed"></app-proposal-nav-marker>
          </button>
        }
      </div>
    </div>

    <!-- Right content panel -->
    <div class="flex-1 min-w-0 border border-gray-200 rounded-lg overflow-y-auto">
      @if (showValidation && validationErrors.length > 0) {
        <div class="m-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p class="text-sm font-medium text-yellow-800 mb-1">
            Das Regelset ist noch unvollständig. Bitte ergänze:
          </p>
          <ul class="list-disc list-inside space-y-1">
            @for (error of validationErrors; track error) {
              <li class="text-sm text-yellow-800">{{ error }}</li>
            }
          </ul>
        </div>
      }

      <!-- ===== General Info ===== -->
      @if (selectedSection === 'general') {
        <div class="flex flex-col">
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faCircleInfo" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col">
              <span class="text-sm font-medium text-gray-900">Allgemeine Angaben</span>
              <span class="text-sm text-gray-500">Bezeichnung und Klassifikation</span>
            </div>
          </div>
          <div class="flex flex-col gap-4 p-4" [formGroup]="form">
            <app-change-marker (acceptClicked)="onFieldAccepted('name')" (rejectClicked)="onFieldRejected('name')"
                               label="myCheck Name" reviewKey="field:name"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('name'))">
              <bic-input formControlName="name" label="myCheck Name" placeholder="Name eingeben"></bic-input>
            </app-change-marker>
            <app-change-marker (acceptClicked)="onFieldAccepted('description')" (rejectClicked)="onFieldRejected('description')"
                               label="myCheck Beschreibung" reviewKey="field:description"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('description'))">
              <bic-input formControlName="description" label="myCheck Beschreibung"
                         placeholder="Beschreibung eingeben"></bic-input>
            </app-change-marker>
            <app-change-marker (acceptClicked)="onFieldAccepted('configJson.allowedFileTypes')" (rejectClicked)="onFieldRejected('configJson.allowedFileTypes')"
                               label="Erlaubte Dateitypen" reviewKey="field:configJson.allowedFileTypes"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.allowedFileTypes'))">
              <bic-select (valueChange)="updateAllowedFileTypes($event)"
                          [class.force-value-color]="areAllAllowedFileTypesSelected()"
                          [multiple]="true"
                          [onlyShowPlaceholder]="areAllAllowedFileTypesSelected()"
                          [value]="getAllowedFileTypesSelectionValue()"
                          formControlName="allowedFileTypes"
                          label="Erlaubte Dateitypen"
                          [placeholder]="areAllAllowedFileTypesSelected() ? 'Alle erlaubt' : 'Dateitypen auswählen'">
                <bic-dropdown-element
                  #allowedFileTypesSelectAllOption
                  (onClick)="toggleAllAllowedFileTypes()"
                  [selection]="true">{{ getAllowedFileTypesSelectAllLabel() }}</bic-dropdown-element>
                @for (fileType of availableFileTypes; track fileType) {
                  <bic-dropdown-element [selection]="true" [value]="fileType">{{ fileType }}</bic-dropdown-element>
                }
              </bic-select>
            </app-change-marker>
            <app-change-marker (acceptClicked)="onFieldAccepted('configJson.allowedDocumentClasses')" (rejectClicked)="onFieldRejected('configJson.allowedDocumentClasses')"
                               label="Erlaubte Dokumentenklassen" reviewKey="field:configJson.allowedDocumentClasses"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.allowedDocumentClasses'))">
              <bic-select (valueChange)="updateAllowedDocumentClasses($event)"
                          [class.force-value-color]="areAllAllowedDocumentClassesSelected()"
                          [multiple]="true"
                          [onlyShowPlaceholder]="areAllAllowedDocumentClassesSelected()"
                          [value]="getAllowedDocumentClassesSelectionValue()"
                          formControlName="allowedDocumentClasses"
                          label="Erlaubte Dokumentenklassen"
                          [placeholder]="areAllAllowedDocumentClassesSelected() ? 'Alle erlaubt' : 'Dokumentenklassen auswählen'">
                <bic-dropdown-element
                  #allowedDocumentClassesSelectAllOption
                  (onClick)="toggleAllAllowedDocumentClasses()"
                  [selection]="true">{{ getAllowedDocumentClassesSelectAllLabel() }}</bic-dropdown-element>
                @for (documentClass of availableDocumentClasses; track documentClass.id) {
                  <bic-dropdown-element [selection]="true" [value]="documentClass.id">{{ documentClass.name }}</bic-dropdown-element>
                }
              </bic-select>
            </app-change-marker>
            <app-change-marker (acceptClicked)="onFieldAccepted('configJson.checkMode')" (rejectClicked)="onFieldRejected('configJson.checkMode')"
                               label="Prüfmodus" reviewKey="field:configJson.checkMode"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.checkMode'))">
              <div class="flex flex-col gap-1">
                <bic-select formControlName="checkMode" label="Prüfmodus" placeholder="Prüfmodus auswählen">
                  <bic-dropdown-element value="SINGLE">Jede Datei einzeln prüfen</bic-dropdown-element>
                  <bic-dropdown-element value="COMBINED">Alle Dateien zusammen prüfen</bic-dropdown-element>
                </bic-select>
                <span class="text-xs text-gray-500">„Zusammen" prüft jede Prüfregel EINMAL über alle
                  ausgewählten Dateien: die KI sucht sich die einschlägigen Stellen selbst und nennt
                  die herangezogene Datei im Ergebnis. Nur mit Prüfebene „Gesamtdokument" möglich.</span>
              </div>
            </app-change-marker>
            <span
              class="text-xs text-gray-500 -mt-2">Auf welcher Ebene sollen die ausgewählten Dateien geprüft werden?</span>
          </div>
        </div>
      }

      <!-- ===== Regelparameter ===== -->
      @if (selectedSection === 'parameters') {
        <div class="flex flex-col">
          <!-- Header -->
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faGear" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col">
              <span class="text-sm font-medium text-gray-900">Regelparameter</span>
              <span class="text-sm text-gray-500">Notwendige Daten und relevante Parameter zur Regelprüfung</span>
            </div>
          </div>
          <div class="flex flex-col gap-4 p-4">
            <!-- Eingabeparameter (Set-Ebene): Prüfebene + Identifikator + Parameter-Tabelle -->

            <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-gray-900">Eingabeparameter</span>
                  <span class="text-sm text-gray-500">Parameter zur Erfassung der Eingabedaten. Diese können in den
                    Prüfregeln weiterverwendet werden.</span>
                </div>
                <div class="flex flex-row gap-4">
                  <app-change-marker (acceptClicked)="onFieldAccepted('configJson.pruefebene')" (rejectClicked)="onFieldRejected('configJson.pruefebene')"
                               label="Prüfebene" reviewKey="field:configJson.pruefebene"
                                     [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.pruefebene'))" [rootClass]="'flex-1 min-w-0'">
                    <div class="flex-1 min-w-0">
                      <bic-select (valueChange)="onPruefebeneChange($event)"
                                  [value]="pruefebene"
                                  label="Prüfebene"
                                  placeholder="Prüfebene auswählen">
                        @for (option of pruefebeneOptions; track option.value) {
                          <bic-dropdown-element [value]="option.value">{{ option.label }}</bic-dropdown-element>
                        }
                      </bic-select>
                      <span class="text-xs text-gray-500">Ebene auf welcher geprüft werden soll (Gesamtdokument → 3 Spalten,
                        sonst 4 Spalten im Bericht).</span>
                    </div>
                  </app-change-marker>
                  @if (pruefebene !== 'gesamtdokument') {
                    <app-change-marker (acceptClicked)="onFieldAccepted('configJson.identifikatorParamId')" (rejectClicked)="onFieldRejected('configJson.identifikatorParamId')"
                               label="Identifikator" reviewKey="field:configJson.identifikatorParamId"
                                       [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.identifikatorParamId'))" [rootClass]="'w-[320px] shrink-0'">
                      <div class="w-[320px] shrink-0">
                        <bic-select (valueChange)="setIdentifikator($event)"
                                    [value]="identifikatorParamId"
                                    [disabled]="!identifikatorOptions.length"
                                    label="Identifikator"
                                    placeholder="Parameter auswählen">
                          @for (option of identifikatorOptions; track option.id) {
                            <bic-dropdown-element [value]="option.id">{{ option.name }}</bic-dropdown-element>
                          }
                        </bic-select>
                        <span class="text-xs text-gray-500">Parameter zur Identifizierung jeder Prüfeinheit.</span>
                      </div>
                    </app-change-marker>
                  }
                </div>
                @if (pruefebene !== 'gesamtdokument') {
                  <app-change-marker (acceptClicked)="onFieldAccepted('configJson.pruefebeneFilter')" (rejectClicked)="onFieldRejected('configJson.pruefebeneFilter')"
                               label="Filter (optional)" reviewKey="field:configJson.pruefebeneFilter"
                                     [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.pruefebeneFilter'))">
                    <div class="flex flex-col gap-1">
                      <bic-textarea [(value)]="pruefebeneFilter"
                                    label="Filter (optional)"
                                    placeholder="z. B. nur Kapitel zum Brandschutz, nur LV-Positionen zu Stahlbetonbauteilen, nur Elemente der Kostengruppe 300"
                                    [rows]="2"></bic-textarea>
                      <span class="text-xs text-gray-500">KI-Anweisung, welche Einheiten dieser Prüfebene extrahiert
                        werden sollen. Leer lassen, um alle zu prüfen.</span>
                    </div>
                  </app-change-marker>
                }
              <div [style.padding-right.px]="rowGutter('eingabeparameter')"
                   class="flex flex-row gap-4 text-xs font-medium text-gray-500 uppercase">
                  <span [tooltip]="'Bezeichnung'" class="flex-1 min-w-0 truncate">Bezeichnung</span>
                  <span [tooltip]="'Beschreibung'" class="flex-[2] min-w-0 truncate">Beschreibung</span>
                  <span [tooltip]="'Datentyp'" class="w-[200px] min-w-0 truncate">Datentyp</span>
                  <span class="w-[40px]"></span>
                </div>
              <ng-template #inputParamRow let-i="index" let-param let-removed="removed">
                  <div class="flex flex-row gap-4 items-center">
                    <app-change-marker [actions]="false" [change]="rowFieldChange('eingabeparameter', param, i, 'name')"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [(value)]="param.name" [showX]="false" placeholder="Bezeichnung"></bic-input>
                    </app-change-marker>
                    <app-change-marker [actions]="false"
                                       [change]="rowFieldChange('eingabeparameter', param, i, 'beschreibung')"
                                       rootClass="flex-[2] min-w-0">
                      <bic-input [(value)]="param.beschreibung" [showX]="false"
                                 placeholder="Beschreibung"></bic-input>
                    </app-change-marker>
                    <app-change-marker [actions]="false"
                                       [change]="rowFieldChange('eingabeparameter', param, i, 'dataType')"
                                       rootClass="w-[200px] shrink-0">
                      <bic-select (valueChange)="updateEingabeparameterDataType(param, $event)"
                                  [disabled]="isIdentifikator(param)"
                                  [value]="param.dataType"
                                  placeholder="Datentyp">
                        @for (type of eingabeParameterTypes; track type) {
                          <bic-dropdown-element [value]="type">{{ type }}</bic-dropdown-element>
                        }
                      </bic-select>
                    </app-change-marker>
                    <!-- Optionen nur bei Datentyp „Auswahl": sie erzwingen zur Laufzeit das
                         Extraktionsergebnis (Enum im Antwortschema). -->
                    @if (needsOptions(param)) {
                      <app-change-marker [actions]="false"
                                         [change]="rowFieldChange('eingabeparameter', param, i, 'options')"
                                         rootClass="shrink-0">
                        <button (click)="openOptionsPopup('input', param)"
                                [class.border-gray-300]="optionsPreview(param).length"
                                [class.border-red]="!optionsPreview(param).length"
                                [class.text-gray-900]="optionsPreview(param).length"
                                [class.text-red]="!optionsPreview(param).length"
                                class="h-[38px] px-3 py-2 border rounded-lg bg-white inline-flex items-center gap-2 text-sm font-medium hover:bg-gray-50 transition"
                                data-proposal-mark>
                          <fa-icon [icon]="faPenToSquare" class="text-sm"></fa-icon>
                          {{ optionsPreview(param).length ? optionsPreview(param).join(' · ') : 'Optionen fehlen' }}
                        </button>
                      </app-change-marker>
                    }
                    @if (removed) {
                      <div class="w-[40px]"></div>
                    } @else {
                      <button (click)="removeEingabeparameter(i)"
                              class="w-[40px] flex justify-center text-gray-300 hover:text-gray-500">
                        <fa-icon [icon]="faTrash"></fa-icon>
                      </button>
                    }
                  </div>
              </ng-template>
              @for (param of eingabeparameter; track param.id) {
                <app-proposal-row (acceptClicked)="acceptRow('eingabeparameter', param)"
                                  (rejectClicked)="rejectRow('eingabeparameter', param)"
                                  [attr.data-proposal-review]="'row:eingabeparameter:' + $index"
                                  [pending]="rowPending('eingabeparameter', param)"
                                  [reserveSpace]="rowGutter('eingabeparameter') > 0">
                  <ng-container [ngTemplateOutletContext]="{$implicit: param, index: $index, removed: false}"
                                [ngTemplateOutlet]="inputParamRow"></ng-container>
                </app-proposal-row>
                }
              <!-- Vom Vorschlag entfernte Zeilen: ✓ bestätigt das Entfernen, ✗ stellt die Zeile wieder her. -->
              @for (entry of removedRows('eingabeparameter'); track entry) {
                <app-proposal-row (acceptClicked)="acceptRowRemoval('eingabeparameter', entry)"
                                  (rejectClicked)="rejectRowRemoval('eingabeparameter', entry)"
                                  [attr.data-proposal-review]="'row:eingabeparameter:removed:' + entry.index"
                                  [removed]="true" [reserveSpace]="true">
                  <ng-container [ngTemplateOutletContext]="{$implicit: entry.before, index: -1, removed: true}"
                                [ngTemplateOutlet]="inputParamRow"></ng-container>
                </app-proposal-row>
              }
            </div>

            <button class="flex flex-row items-center justify-center gap-2 py-2 text-sm font-medium text-gray-400 hover:text-gray-600"
                    (click)="addEingabeparameter()">
              <fa-icon [icon]="faPlus"></fa-icon>
              Eingabeparameter hinzufügen
            </button>

            <!-- Prüfparameter -->

            <div class="flex flex-col gap-4 pt-4 border-t border-gray-200">
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-gray-900">Prüfparameter</span>
                  <span class="text-sm text-gray-500">Parameter zur Validierung der Eingabedaten</span>
                </div>
              <div [style.padding-right.px]="rowGutter('checkParameters')"
                   class="flex flex-row gap-4 text-xs font-medium text-gray-500 uppercase">
                  <span [tooltip]="'Bezeichnung'" class="flex-1 min-w-0 truncate">Bezeichnung</span>
                  <span [tooltip]="'Datentyp'" class="flex-1 min-w-0 truncate">Datentyp</span>
                  <span [tooltip]="'Notwendigkeit'" class="flex-1 min-w-0 truncate">Notwendigkeit</span>
                  <span class="w-[40px]"></span>
                </div>
              <ng-template #checkParamRow let-i="index" let-param let-removed="removed">
                  <div class="flex flex-row gap-4 items-center">
                    <app-change-marker [actions]="false" [change]="rowFieldChange('checkParameters', param, i, 'name')"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [(value)]="param.name" [showX]="false" placeholder="Bezeichnung"></bic-input>
                    </app-change-marker>
                    <app-change-marker [actions]="false"
                                       [change]="rowFieldChange('checkParameters', param, i, 'dataType')"
                                       rootClass="flex-1 min-w-0">
                      <bic-select [(value)]="param.dataType" placeholder="Datentyp">
                        @for (dt of parameterDataTypes; track dt) {
                          <bic-dropdown-element [value]="dt">{{ dt }}</bic-dropdown-element>
                        }
                      </bic-select>
                    </app-change-marker>
                    <app-change-marker [actions]="false"
                                       [change]="rowFieldChange('checkParameters', param, i, 'necessity')"
                                       rootClass="flex-1 min-w-0">
                      <bic-select [(value)]="param.necessity" placeholder="Notwendigkeit">
                        <bic-dropdown-element value="Pflicht">Pflicht</bic-dropdown-element>
                        <bic-dropdown-element value="Optional">Optional</bic-dropdown-element>
                      </bic-select>
                    </app-change-marker>
                    <!-- Bei „Auswahl" wählt der Nutzer den Wert je Prüfregel aus dieser Liste
                         (statt ihn zu tippen) – dasselbe Muster wie bei den Tags. -->
                    @if (needsOptions(param)) {
                      <app-change-marker [actions]="false"
                                         [change]="rowFieldChange('checkParameters', param, i, 'options')"
                                         rootClass="shrink-0">
                        <button (click)="openOptionsPopup('check', param)"
                                [class.border-gray-300]="optionsPreview(param).length"
                                [class.border-red]="!optionsPreview(param).length"
                                [class.text-gray-900]="optionsPreview(param).length"
                                [class.text-red]="!optionsPreview(param).length"
                                class="h-[38px] px-3 py-2 border rounded-lg bg-white inline-flex items-center gap-2 text-sm font-medium hover:bg-gray-50 transition"
                                data-proposal-mark>
                          <fa-icon [icon]="faPenToSquare" class="text-sm"></fa-icon>
                          {{ optionsPreview(param).length ? 'Auswahloptionen (' + optionsPreview(param).length + ')' : 'Optionen fehlen' }}
                        </button>
                      </app-change-marker>
                    }
                    @if (removed || checkParameters.length <= 1) {
                      <div class="w-[40px]"></div>
                    } @else {
                      <button (click)="removeCheckParameter(i)"
                              class="w-[40px] flex justify-center text-gray-300 hover:text-gray-500">
                        <fa-icon [icon]="faTrash"></fa-icon>
                      </button>
                    }
                  </div>
              </ng-template>
              @for (param of checkParameters; track param.id) {
                <app-proposal-row (acceptClicked)="acceptRow('checkParameters', param)"
                                  (rejectClicked)="rejectRow('checkParameters', param)"
                                  [attr.data-proposal-review]="'row:checkParameters:' + $index"
                                  [pending]="rowPending('checkParameters', param)"
                                  [reserveSpace]="rowGutter('checkParameters') > 0">
                  <ng-container [ngTemplateOutletContext]="{$implicit: param, index: $index, removed: false}"
                                [ngTemplateOutlet]="checkParamRow"></ng-container>
                </app-proposal-row>
                }
              <!-- Vom Vorschlag entfernte Zeilen: ✓ bestätigt das Entfernen, ✗ stellt die Zeile wieder her. -->
              @for (entry of removedRows('checkParameters'); track entry) {
                <app-proposal-row (acceptClicked)="acceptRowRemoval('checkParameters', entry)"
                                  (rejectClicked)="rejectRowRemoval('checkParameters', entry)"
                                  [attr.data-proposal-review]="'row:checkParameters:removed:' + entry.index"
                                  [removed]="true" [reserveSpace]="true">
                  <ng-container [ngTemplateOutletContext]="{$implicit: entry.before, index: -1, removed: true}"
                                [ngTemplateOutlet]="checkParamRow"></ng-container>
                </app-proposal-row>
              }
            </div>

            <button class="flex flex-row items-center justify-center gap-2 py-2 text-sm font-medium text-gray-400 hover:text-gray-600"
                    (click)="addCheckParameter()">
              <fa-icon [icon]="faPlus"></fa-icon>
              Prüfparameter hinzufügen
            </button>

            <!-- Tags -->

            <div class="flex flex-col gap-4 pt-4 border-t border-gray-200">
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-gray-900">Tags</span>
                  <span class="text-sm text-gray-500">Tags zur Organisation und Verwaltung der Prüfregel</span>
                </div>
              <div [style.padding-right.px]="rowGutter('tags')"
                   class="flex flex-row gap-4 text-xs font-medium text-gray-500 uppercase">
                  <span [tooltip]="'Bezeichnung'" class="flex-1 min-w-0 truncate">Bezeichnung</span>
                  <span [tooltip]="'Notwendigkeit'" class="flex-1 min-w-0 truncate">Notwendigkeit</span>
                  <span [tooltip]="'Datentyp'" class="w-[168px] min-w-0 truncate">Datentyp</span>
                  <span class="w-[40px]"></span>
                </div>
              <ng-template #tagRow let-i="index" let-removed="removed" let-tag>
                  <div class="flex flex-row gap-4 items-center">
                    <app-change-marker [actions]="false" [change]="rowFieldChange('tags', tag, i, 'name')"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [(value)]="tag.name" [showX]="false" placeholder="Bezeichnung"></bic-input>
                    </app-change-marker>
                    <app-change-marker [actions]="false" [change]="rowFieldChange('tags', tag, i, 'necessity')"
                                       rootClass="flex-1 min-w-0">
                      <bic-select [(value)]="tag.necessity" placeholder="Notwendigkeit">
                        <bic-dropdown-element value="Pflicht">Pflicht</bic-dropdown-element>
                        <bic-dropdown-element value="Optional">Optional</bic-dropdown-element>
                      </bic-select>
                    </app-change-marker>
                    <div class="w-[168px] flex justify-end">
                      <app-change-marker [actions]="false" [change]="rowFieldChange('tags', tag, i, 'options')"
                                         rootClass="shrink-0">
                        <button
                          (click)="openOptionsPopup('tag', tag)"
                          class="h-[38px] px-3 py-2 border border-gray-300 rounded-lg bg-white inline-flex items-center justify-center gap-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition"
                          data-proposal-mark>
                          <fa-icon [icon]="faPenToSquare" class="text-sm"></fa-icon>
                          Auswahloptionen
                        </button>
                      </app-change-marker>
                    </div>
                    @if (removed) {
                      <div class="w-[40px]"></div>
                    } @else {
                      <button (click)="removeTag(i)"
                              class="w-[40px] flex justify-center text-gray-300 hover:text-gray-500">
                        <fa-icon [icon]="faTrash"></fa-icon>
                      </button>
                    }
                  </div>
              </ng-template>
              @for (tag of tags; track tag.id) {
                <app-proposal-row (acceptClicked)="acceptRow('tags', tag)" (rejectClicked)="rejectRow('tags', tag)"
                                  [attr.data-proposal-review]="'row:tags:' + $index"
                                  [pending]="rowPending('tags', tag)" [reserveSpace]="rowGutter('tags') > 0">
                  <ng-container [ngTemplateOutletContext]="{$implicit: tag, index: $index, removed: false}"
                                [ngTemplateOutlet]="tagRow"></ng-container>
                </app-proposal-row>
                }
              <!-- Vom Vorschlag entfernte Zeilen: ✓ bestätigt das Entfernen, ✗ stellt die Zeile wieder her. -->
              @for (entry of removedRows('tags'); track entry) {
                <app-proposal-row (acceptClicked)="acceptRowRemoval('tags', entry)"
                                  (rejectClicked)="rejectRowRemoval('tags', entry)"
                                  [attr.data-proposal-review]="'row:tags:removed:' + entry.index"
                                  [removed]="true" [reserveSpace]="true">
                  <ng-container [ngTemplateOutletContext]="{$implicit: entry.before, index: -1, removed: true}"
                                [ngTemplateOutlet]="tagRow"></ng-container>
                </app-proposal-row>
              }
            </div>

            <button class="flex flex-row items-center justify-center gap-2 py-2 text-sm font-medium text-gray-400 hover:text-gray-600"
                    (click)="addTag()">
              <fa-icon [icon]="faPlus"></fa-icon>
              Tags hinzufügen
            </button>
          </div>
        </div>
      }

      <!-- ===== Systemprompt ===== -->
      @if (selectedSection === 'systemprompt') {
        <div class="flex flex-col min-w-0 h-full">
          <!-- Header -->
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200 shrink-0">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faSparkles" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-sm font-medium text-gray-900 leading-[1.5]">Systemprompt</span>
              <span class="text-sm text-gray-500 leading-[1.5]">Konfiguration des KI-Agenten</span>
            </div>
            <button
              (click)="openPromptSuggestionPopup()"
              class="flex items-center gap-1 text-xs text-gray-500 hover:underline shrink-0"
              type="button">
              <fa-icon [icon]="faCircleInfo" class="text-xs"></fa-icon>
              Vorschlag anzeigen
            </button>
          </div>
          <div class="flex flex-col gap-4 p-4 flex-1 min-h-0 overflow-y-auto">
            <!-- KI-Modell: Badge oder Dropdown, je nach Freischaltung -->
            <app-change-marker (acceptClicked)="onFieldAccepted('configJson.kiModel')" (rejectClicked)="onFieldRejected('configJson.kiModel')"
                               label="KI-Modell" reviewKey="field:configJson.kiModel"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.kiModel'))">
              <app-ai-model-field (valueChange)="onAiModelChange($event)" [value]="aiModel"/>
            </app-change-marker>
            <!-- Systemprompt: min-h statt min-h-0, sonst schrumpft der Abschnitt unter seinen Inhalt, ragt in den
                 nächsten hinein und der äußere Scroll-Container läuft nie über. -->
            <div class="flex flex-col gap-2 flex-1 min-h-[260px]">
              <div class="flex flex-col shrink-0">
                <!-- Kopfzeile wie bei den übrigen Feldern (#2789): Label · Badge · Spacer · Aktionen · ✓/✗.
                     Der Marker unten zeigt dafür keine eigene Kopfzeile (externalHeader). -->
                @let systempromptStatus = fieldChangeStatus('configJson.systemprompt');
                <div [style.min-height.px]="systempromptStatus ? 38 : null" class="flex flex-row items-center gap-2"
                     data-proposal-prompt-header="systemprompt">
                  <span class="text-sm font-medium text-gray-900 leading-[1.5]">Systemprompt</span>
                  @if (systempromptStatus) {
                    <app-proposal-status-badge [status]="systempromptStatus"></app-proposal-status-badge>
                  }
                  <span class="flex-1"></span>
                  <div class="flex items-center gap-3">
                    @if (previousSystemprompt === null) {
                      <button
                        (click)="optimizeSystemprompt()"
                        [disabled]="optimizingSystemprompt || !systemprompt.trim()"
                        class="flex items-center gap-1 text-xs text-green hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                        type="button">
                        <fa-icon [class.animate-spin]="optimizingSystemprompt"
                                 [icon]="optimizingSystemprompt ? faSpinner : faSparkles" class="text-xs"></fa-icon>
                        {{ optimizingSystemprompt ? 'Verbessere…' : 'Mit chatBIC verbessern' }}
                      </button>
                    } @else {
                      <button
                        (click)="restoreSystemprompt()"
                        class="text-xs text-gray-500 hover:underline"
                        type="button">
                        Wiederherstellen
                      </button>
                    }
                  </div>
                  @if (systempromptStatus) {
                    <app-proposal-actions (acceptClicked)="onFieldAccepted('configJson.systemprompt')"
                                          (rejectClicked)="onFieldRejected('configJson.systemprompt')"></app-proposal-actions>
                  }
                </div>
                <span class="text-sm text-gray-500 leading-[1.5]">Legt Rolle, Verhalten und Rahmenbedingungen des KI-Agenten fest.</span>
              </div>
              <app-change-marker (acceptClicked)="onFieldAccepted('configJson.systemprompt')" (rejectClicked)="onFieldRejected('configJson.systemprompt')"
                                 reviewKey="field:configJson.systemprompt"
                                 [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.systemprompt'))" [fill]="true"
                                 [externalHeader]="true" [textDiff]="true">
                <bic-textarea (click)="onSystempromptCursorChange($event)"
                              (input)="onSystempromptChange($event)"
                              (keyup)="onSystempromptCursorChange($event)"
                              class="prompt-textarea-fill"
                              [rows]="8"
                              [value]="systemprompt"></bic-textarea>
              </app-change-marker>
            </div>
            <!-- Variable badges for Systemprompt -->
            <div class="flex flex-row flex-wrap gap-2 shrink-0">
              @for (variable of promptVariables; track variable) {
                <button
                  (click)="appendPromptVariable('system', variable)"
                  class="px-2.5 py-0.5 rounded-md bg-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-300 transition-colors"
                  type="button">
                  {{ variable }}
                </button>
              }
            </div>

            <!-- Prüfprompt -->
            <div class="flex flex-col gap-2 flex-1 min-h-[260px]">
              <div class="flex flex-col shrink-0">
                <!-- Kopfzeile wie bei den übrigen Feldern (#2789): Label · Badge · Spacer · Aktionen · ✓/✗.
                     Der Marker unten zeigt dafür keine eigene Kopfzeile (externalHeader). -->
                @let pruefpromptStatus = fieldChangeStatus('configJson.pruefprompt');
                <div [style.min-height.px]="pruefpromptStatus ? 38 : null" class="flex flex-row items-center gap-2"
                     data-proposal-prompt-header="pruefprompt">
                  <span class="text-sm font-medium text-gray-900 leading-[1.5]">Prüfprompt</span>
                  @if (pruefpromptStatus) {
                    <app-proposal-status-badge [status]="pruefpromptStatus"></app-proposal-status-badge>
                  }
                  <span class="flex-1"></span>
                  <div class="flex items-center gap-3">
                    @if (previousPruefprompt === null) {
                      <button
                        (click)="optimizePruefprompt()"
                        [disabled]="optimizingPruefprompt || !pruefprompt.trim()"
                        class="flex items-center gap-1 text-xs text-green hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                        type="button">
                        <fa-icon [class.animate-spin]="optimizingPruefprompt"
                                 [icon]="optimizingPruefprompt ? faSpinner : faSparkles" class="text-xs"></fa-icon>
                        {{ optimizingPruefprompt ? 'Verbessere…' : 'Mit chatBIC verbessern' }}
                      </button>
                    } @else {
                      <button
                        (click)="restorePruefprompt()"
                        class="text-xs text-gray-500 hover:underline"
                        type="button">
                        Wiederherstellen
                      </button>
                    }
                  </div>
                  @if (pruefpromptStatus) {
                    <app-proposal-actions (acceptClicked)="onFieldAccepted('configJson.pruefprompt')"
                                          (rejectClicked)="onFieldRejected('configJson.pruefprompt')"></app-proposal-actions>
                  }
                </div>
                <span class="text-sm text-gray-500 leading-[1.5]">Ergänzt den Systemprompt um die konkreten Prüfschritte, nach denen das Dokument bewertet wird.</span>
              </div>
              <app-change-marker (acceptClicked)="onFieldAccepted('configJson.pruefprompt')" (rejectClicked)="onFieldRejected('configJson.pruefprompt')"
                                 reviewKey="field:configJson.pruefprompt"
                                 [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('configJson.pruefprompt'))" [fill]="true"
                                 [externalHeader]="true" [textDiff]="true">
                <bic-textarea (click)="onPruefpromptCursorChange($event)"
                              (input)="onPruefpromptChange($event)"
                              (keyup)="onPruefpromptCursorChange($event)"
                              class="prompt-textarea-fill"
                              [rows]="5"
                              [value]="pruefprompt"></bic-textarea>
              </app-change-marker>
            </div>
            <!-- Variable badges for Prüfprompt -->
            <div class="flex flex-row flex-wrap gap-2 shrink-0">
              @for (variable of promptVariables; track variable) {
                <button
                  (click)="appendPromptVariable('pruef', variable)"
                  class="px-2.5 py-0.5 rounded-md bg-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-300 transition-colors"
                  type="button">
                  {{ variable }}
                </button>
              }
            </div>

            <!-- Hintergrundwissen / Kontext -->

            <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-gray-900">Hintergrundwissen / Kontext</span>
                  <span class="text-sm text-gray-500">Dateien zur Wissenserweiterung des KI-Agenten</span>
                </div>
                @for (file of contextFiles; track $index) {
                  <app-change-marker (acceptClicked)="acceptRow('contextFiles', file)"
                                     (rejectClicked)="rejectRow('contextFiles', file)"
                                     [attr.data-proposal-review]="'row:contextFiles:' + $index"
                                     [block]="true" [change]="rowChange('contextFiles', file, $index)">
                  <div class="flex flex-row items-center gap-4 px-4 py-2 border border-gray-200 rounded-lg min-w-0">
                    <fa-icon [icon]="faFile" class="text-gray-400 shrink-0"></fa-icon>
                    <span class="text-sm font-medium w-[80px] shrink-0">{{ file.label }}</span>
                    <span class="text-sm font-medium text-gray-900 flex-1 truncate min-w-0">{{ file.name }}</span>
                    <span class="text-sm font-medium shrink-0">{{ file.type }}</span>
                    <span class="text-sm font-medium shrink-0">{{ file.size }}</span>
                    <span class="text-sm font-medium shrink-0">{{ file.date }}</span>
                    <button class="text-gray-300 hover:text-gray-500 shrink-0" (click)="removeContextFile($index)">
                      <fa-icon [icon]="faTrash" class="text-xs"></fa-icon>
                    </button>
                  </div>
                  </app-change-marker>
                }
              <!-- Vom Vorschlag entfernte Zeilen: ✓ bestätigt das Entfernen, ✗ stellt die Zeile wieder her. -->
              @for (entry of removedRows('contextFiles'); track entry) {
                <div [attr.data-proposal-review]="'row:contextFiles:removed:' + entry.index"
                     class="flex flex-row items-center gap-2 p-3 border border-red-600 rounded-lg">
                  <fa-icon [icon]="faTrash" class="text-sm text-red-600 shrink-0"></fa-icon>
                  <span
                    class="text-sm font-medium flex-1 min-w-0 truncate text-gray-500 line-through">{{ entry.before.name || 'Unbenannter Eintrag' }}</span>
                  <app-proposal-status-badge class="shrink-0" status="removed"></app-proposal-status-badge>
                  <app-proposal-actions (acceptClicked)="acceptRowRemoval('contextFiles', entry)"
                                        (rejectClicked)="rejectRowRemoval('contextFiles', entry)"
                                        acceptLabel="Löschen übernehmen"
                                        rejectLabel="Behalten"></app-proposal-actions>
              </div>
              }
            </div>

            <bic-button
              (onClick)="addContextFile()"
              [disabled]="contextFiles.length >= MAX_CONTEXT_FILES"
              buttonType="white">
              <fa-icon [icon]="faPlus" iconLeft></fa-icon>
              Datei auswählen
            </bic-button>
          </div>
        </div>
      }

      <!-- ===== Ausgabeformat ===== -->
      @if (selectedSection === 'output-format') {
        <div class="flex flex-col">
          <!-- Header -->
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faFile" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-sm font-medium text-gray-900">Ausgabeformat</span>
              <span class="text-sm text-gray-500">Konfiguration des Ausgabeformates der KI-generierten Antwort</span>
            </div>
          </div>
          <div class="flex flex-col gap-4 p-4">
            <!-- Table header -->
            <div [style.padding-right.px]="rowGutter('outputParameters')" class="flex flex-row items-center gap-4">
              <span [tooltip]="'Modus'" class="w-[200px] text-xs font-medium text-gray-500 uppercase min-w-0 truncate">Modus</span>
              <span [tooltip]="'Name'" class="flex-1 text-xs font-medium text-gray-500 uppercase min-w-0 truncate">Name</span>
              <span [tooltip]="'Wert (Anweisung an die KI)'" class="flex-1 text-xs font-medium text-gray-500 uppercase min-w-0 truncate">Wert (Anweisung an die KI)</span>
              <span [tooltip]="'Datentyp'" class="w-[200px] text-xs font-medium text-gray-500 uppercase min-w-0 truncate">Datentyp</span>
              <span class="w-[40px]"></span>
            </div>
            <!-- Eine Zeile bleibt in jedem Fall dieselbe Zeile: ✓/✗ links, geänderte Felder einzeln grün
                 markiert. Eine vom Vorschlag entfernte Zeile erscheint mit ihrem alten Inhalt, durchgestrichen
                 und rot. -->
            <ng-template #outputRow let-i="index" let-param let-removed="removed">
              <div class="flex flex-row items-center gap-4">
                <app-change-marker [actions]="false" [change]="rowFieldChange('outputParameters', param, i, 'modus')"
                                   rootClass="w-[200px] shrink-0">
                  <bic-select (valueChange)="onOutputModusChange(param, $event)"
                              [disabled]="isLockedOutputParameter(param)"
                              [value]="param.modus"
                              placeholder="Modus">
                    @for (opt of modusOptions; track opt) {
                      <bic-dropdown-element [value]="opt">{{ opt }}</bic-dropdown-element>
                    }
                  </bic-select>
                </app-change-marker>
                <!-- Im Modus „Prüfparameter" gibt es keine Wert-Spalte: der Select überspannt Name + Wert. -->
                <app-change-marker [actions]="false" [change]="rowFieldChange('outputParameters', param, i, 'name')"
                                   [rootClass]="param.modus === 'Prüfparameter' ? 'flex-[2] min-w-0' : 'flex-1 min-w-0'">
                  @if (param.modus === 'Prüfparameter') {
                    <bic-select [(value)]="param.name" placeholder="Prüfparameter auswählen">
                      @for (checkParam of checkParameters; track checkParam.id) {
                        @if (checkParam.name?.trim()) {
                          <bic-dropdown-element [value]="checkParam.name">{{ checkParam.name }}</bic-dropdown-element>
                        }
                      }
                    </bic-select>
                  } @else {
                    <bic-input [(value)]="param.name"
                               [disabled]="isLockedOutputParameter(param)"
                               [showX]="false"
                               placeholder="Name"></bic-input>
                  }
                </app-change-marker>
                @if (param.modus !== 'Prüfparameter') {
                  <app-change-marker [actions]="false" [change]="rowFieldChange('outputParameters', param, i, 'wert')"
                                     rootClass="flex-1 min-w-0">
                    <bic-input [(value)]="param.wert"
                               [disabled]="isLockedOutputParameter(param)"
                               [showX]="false"
                               [placeholder]="param.modus === 'Eigener Wert' ? 'Wert' : 'Wert (Anweisung an die KI)'"></bic-input>
                  </app-change-marker>
                }
                <app-change-marker [actions]="false" [change]="rowFieldChange('outputParameters', param, i, 'dateityp')"
                                   rootClass="w-[200px] shrink-0">
                  <bic-select [(value)]="param.dateityp"
                              [disabled]="isDateitypDisabled(param)"
                              placeholder="Datentyp">
                    @for (opt of dateitypOptions; track opt) {
                      <bic-dropdown-element [value]="opt">{{ opt }}</bic-dropdown-element>
                    }
                  </bic-select>
                </app-change-marker>
                <!-- Bei „Auswahl" MUSS die KI genau einen dieser Werte liefern (Enum im
                     Antwortschema). Ohne Optionen wäre der Datentyp wirkungslos, deshalb ist die
                     Zeile bis dahin rot und das Speichern blockiert. -->
                @if (needsOptions(param)) {
                  <app-change-marker [actions]="false"
                                     [change]="rowFieldChange('outputParameters', param, i, 'options')"
                                     rootClass="shrink-0">
                    <button (click)="openOptionsPopup('output', param)"
                            [class.border-gray-300]="optionsPreview(param).length"
                            [class.border-red]="!optionsPreview(param).length"
                            [class.text-gray-900]="optionsPreview(param).length"
                            [class.text-red]="!optionsPreview(param).length"
                            class="h-[38px] px-3 py-2 border rounded-lg bg-white inline-flex items-center gap-2 text-sm font-medium hover:bg-gray-50 transition shrink-0"
                            data-proposal-mark>
                      <fa-icon [icon]="faPenToSquare" class="text-sm"></fa-icon>
                      {{ optionsPreview(param).length ? optionsPreview(param).join(' · ') : 'Optionen fehlen' }}
                    </button>
                  </app-change-marker>
                }
                @if (removed || isLockedOutputParameter(param)) {
                  <div class="w-[40px]"></div>
                } @else {
                  <button (click)="removeOutputParameter(i)"
                          class="w-[40px] flex justify-center text-gray-300 hover:text-gray-500">
                    <fa-icon [icon]="faTrash"></fa-icon>
                  </button>
                }
              </div>
            </ng-template>
            <!-- Rows -->
            @for (param of outputParameters; track $index) {
              <app-proposal-row (acceptClicked)="acceptRow('outputParameters', param)"
                                (rejectClicked)="rejectRow('outputParameters', param)"
                                  [attr.data-proposal-review]="'row:outputParameters:' + $index"
                                [pending]="rowPending('outputParameters', param)"
                                [reserveSpace]="rowGutter('outputParameters') > 0">
                <ng-container [ngTemplateOutletContext]="{$implicit: param, index: $index, removed: false}"
                              [ngTemplateOutlet]="outputRow"></ng-container>
              </app-proposal-row>
            }
            <!-- Vom Vorschlag entfernte Zeilen: ✓ bestätigt das Entfernen, ✗ stellt die Zeile wieder her. -->
            @for (entry of removedRows('outputParameters'); track entry) {
              <app-proposal-row (acceptClicked)="acceptRowRemoval('outputParameters', entry)"
                                (rejectClicked)="rejectRowRemoval('outputParameters', entry)"
                                  [attr.data-proposal-review]="'row:outputParameters:removed:' + entry.index"
                                [removed]="true" [reserveSpace]="true">
                <ng-container [ngTemplateOutletContext]="{$implicit: entry.before, index: -1, removed: true}"
                              [ngTemplateOutlet]="outputRow"></ng-container>
              </app-proposal-row>
            }
          </div>
          <div class="flex flex-col p-4 pt-0">
            <button class="flex flex-row items-center justify-center gap-2 py-2 text-sm font-medium text-gray-400 hover:text-gray-600"
                    (click)="addOutputParameter()">
              <fa-icon [icon]="faPlus"></fa-icon>
              Ausgabeparameter hinzufügen
            </button>
          </div>
        </div>
      }

      <!-- ===== Von chatBIC entfernte Regel ===== -->
      @if (selectedSection === 'removed-rule' && selectedRemovedRule; as removedRule) {
        <div class="p-4">
          <app-removed-rule-card (removeClicked)="onRemovedRuleAccepted(removedRule)"
                                 (restoreClicked)="onRemovedRuleRestored(removedRule)"
                                 [itemName]="getRemovedRuleCardLabel(removedRule)">
          </app-removed-rule-card>
        </div>
      }

      <!-- ===== Rule detail ===== -->
      @if (selectedSection === 'rule' && selectedRule) {
        <!-- Force re-creation on rule switch so bic-select picks up new [value] -->
        @for (ruleKey of [selectedRuleKey]; track ruleKey) {
        <div class="flex flex-col">
          <!-- Header -->
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faCube" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-sm font-medium text-gray-900">Prüfanforderungen</span>
              <span class="text-sm text-gray-500">Definition der Anforderungen</span>
            </div>
          </div>
          <div class="flex flex-col gap-4 p-4">
            <div class="grid gap-4 items-end" style="grid-template-columns: 250px 1fr;">
              <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'configJson.ruleShortCode')" (rejectClicked)="onRuleFieldRejected(selectedRule, 'configJson.ruleShortCode')"
                                 [change]="ruleFieldChange(selectedRule, 'configJson.ruleShortCode')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':configJson.ruleShortCode'" label="Prüfregel Kürzel (max. 10 Zeichen)">
                <bic-input (valueChange)="onSelectedRuleShortCodeChange($event)"
                           [maxLength]="10"
                           [value]="selectedRule.ruleShortCode ?? ''"
                           placeholder="Kürzel eingeben"
                           label="Prüfregel Kürzel (max. 10 Zeichen)"></bic-input>
              </app-change-marker>
              <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'name')" (rejectClicked)="onRuleFieldRejected(selectedRule, 'name')"
                                 [change]="ruleFieldChange(selectedRule, 'name')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':name'" label="Prüfregel Name">
                <bic-input (valueChange)="onSelectedRuleNameChange($event)"
                           [value]="selectedRule.name"
                           placeholder="Name eingeben"
                           label="Prüfregel Name"></bic-input>
              </app-change-marker>
            </div>

            <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'description')" (rejectClicked)="onRuleFieldRejected(selectedRule, 'description')"
                               [change]="ruleFieldChange(selectedRule, 'description')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':description'" label="Prüfregel Beschreibung">
              <bic-input (valueChange)="onSelectedRuleDescriptionChange($event)"
                         [value]="selectedRule.description"
                         placeholder="Beschreibung eingeben"
                         label="Prüfregel Beschreibung"></bic-input>
            </app-change-marker>

            <div class="flex gap-4 items-end">
              <div class="w-1/2">
                <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'configJson.allowedFileTypes')"
                                   (rejectClicked)="onRuleFieldRejected(selectedRule, 'configJson.allowedFileTypes')"
                                   [change]="ruleFieldChange(selectedRule, 'configJson.allowedFileTypes')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':configJson.allowedFileTypes'" label="Dateitypen">
                  <bic-select (valueChange)="updateSelectedRuleAllowedFileTypes($event)"
                              [class.force-value-color]="areAllSelectedRuleAllowedFileTypesSelected()"
                              [multiple]="true"
                              [onlyShowPlaceholder]="areAllSelectedRuleAllowedFileTypesSelected()"
                              [placeholder]="areAllSelectedRuleAllowedFileTypesSelected() ? 'Alle erlaubt' : 'Dateitypen auswählen'"
                              [value]="getSelectedRuleAllowedFileTypesSelectionValue()"
                              label="Dateitypen">
                    <bic-dropdown-element
                      #selectedRuleAllowedFileTypesSelectAllOption
                      (onClick)="toggleAllSelectedRuleAllowedFileTypes()"
                      [selection]="true">Alle erlauben
                    </bic-dropdown-element>
                    @for (fileType of getRuleAllowedFileTypeOptions(); track fileType) {
                      <bic-dropdown-element [selection]="true" [value]="fileType">{{ fileType }}</bic-dropdown-element>
                    }
                  </bic-select>
                </app-change-marker>
              </div>
              <div class="w-1/2">
                <app-change-marker
                  (acceptClicked)="onRuleFieldAccepted(selectedRule, 'configJson.allowedDocumentClasses')"
                  (rejectClicked)="onRuleFieldRejected(selectedRule, 'configJson.allowedDocumentClasses')"
                  [change]="ruleFieldChange(selectedRule, 'configJson.allowedDocumentClasses')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':configJson.allowedDocumentClasses'" label="Dateiklassen">
                  <bic-select (valueChange)="updateSelectedRuleAllowedDocumentClasses($event)"
                              [class.force-value-color]="areAllSelectedRuleAllowedDocumentClassesSelected()"
                              [multiple]="true"
                              [onlyShowPlaceholder]="areAllSelectedRuleAllowedDocumentClassesSelected()"
                              [placeholder]="areAllSelectedRuleAllowedDocumentClassesSelected() ? 'Alle erlaubt' : 'Dateiklassen auswählen'"
                              [value]="getSelectedRuleAllowedDocumentClassesSelectionValue()"
                              label="Dateiklassen">
                    <bic-dropdown-element
                      #selectedRuleAllowedDocumentClassesSelectAllOption
                      (onClick)="toggleAllSelectedRuleAllowedDocumentClasses()"
                      [selection]="true">Alle erlauben
                    </bic-dropdown-element>
                    @for (documentClass of getRuleAllowedDocumentClassOptions(); track documentClass.id) {
                      <bic-dropdown-element [selection]="true" [value]="documentClass.id">{{ documentClass.name }}
                      </bic-dropdown-element>
                    }
                  </bic-select>
                </app-change-marker>
              </div>
            </div>

            <!-- Tags: Entscheidung je Tag (Kopfzeile mit Badge und ✓/✗ am einzelnen Feld). Zu löschende
                 Werte (alter Wert) und offene Werte ohne Tag im Set (aktueller Wert) erscheinen nach der Liste
                 schreibgeschützt. -->
            <div class="flex flex-col gap-2">
              <div class="flex flex-col">
                <span class="text-sm font-medium text-gray-900">Tags</span>
                <span class="text-sm text-gray-500">Tags zur Organisation und Verwaltung der Prüfregel</span>
              </div>
              @for (tag of tags; track tag.id) {
                @if (isRuleEntryListed(selectedRule, 'tagValues', tag.id)) {
                  <app-change-marker (acceptClicked)="onRuleEntryAccepted(selectedRule, 'tagValues', tag.id)"
                                     (rejectClicked)="onRuleEntryRejected(selectedRule, 'tagValues', tag.id)"
                                     [change]="ruleEntryChange(selectedRule, 'tagValues', tag.id)"
                                     [label]="tag.name"
                                     [reviewKey]="'rule:' + selectedRule.id + ':configJson.tagValues:' + tag.id">
                    @if (tag.options?.length) {
                      <bic-select [label]="tag.name" placeholder="Auswahl treffen"
                                  [value]="getTagValue(tag.id)"
                                  (valueChange)="setTagValue(tag.id, $event)">
                        @for (opt of tag.options; track opt) {
                          <bic-dropdown-element [value]="opt">{{ opt }}</bic-dropdown-element>
                        }
                      </bic-select>
                    } @else {
                      <bic-select [label]="tag.name" placeholder="Keine Optionen" [disabled]="true"></bic-select>
                    }
                  </app-change-marker>
                }
              }
              @for (entry of unlistedRuleEntries(selectedRule, 'tagValues'); track entry.key) {
                <app-change-marker (acceptClicked)="onRuleEntryAccepted(selectedRule, 'tagValues', entry.key)"
                                   (rejectClicked)="onRuleEntryRejected(selectedRule, 'tagValues', entry.key)"
                                   [change]="entry.change"
                                   [label]="entry.name"
                                   [reviewKey]="'rule:' + selectedRule.id + ':configJson.tagValues:' + entry.key">
                  <bic-input [disabled]="true" [label]="entry.name" [showX]="false" [value]="entry.value"></bic-input>
                </app-change-marker>
              }
              <!-- Auf Set-Ebene gelöscht, Regelwert unverändert: ✓/✗ sind an die Set-Zeile gekoppelt
                   (setRemovedRuleEntries) und gelten damit für alle Prüfregeln. -->
              @for (entry of setRemovedRuleEntries(selectedRule, 'tagValues'); track entry.key) {
                <app-change-marker (acceptClicked)="acceptRowRemoval('tags', entry.removal)"
                                   (rejectClicked)="rejectRowRemoval('tags', entry.removal)"
                                   [attr.data-proposal-set-removed]="'tagValues:' + entry.key"
                                   [change]="entry.change"
                                   [label]="entry.name">
                  <bic-input [disabled]="true" [label]="entry.name" [showX]="false" [value]="entry.value"></bic-input>
                  <span class="block pt-1 text-xs text-gray-500">Wird unter „Regelparameter“ für alle Prüfregeln gelöscht – ✓/✗ gelten für alle Prüfregeln.</span>
                </app-change-marker>
              }
            </div>

            <!-- Prüfparameter: Entscheidung je Parameter, wie bei den Tags. -->
            <div class="flex flex-col gap-2">
              <div class="flex flex-col">
                <span class="text-sm font-medium text-gray-900">Prüfparameter</span>
                <span class="text-sm text-gray-500">Parameter zur Validierung der Eingabedaten</span>
              </div>
              @for (param of checkParameters; track param.id) {
                @if (isRuleEntryListed(selectedRule, 'parameterValues', param.id)) {
                  <app-change-marker (acceptClicked)="onRuleEntryAccepted(selectedRule, 'parameterValues', param.id)"
                                     (rejectClicked)="onRuleEntryRejected(selectedRule, 'parameterValues', param.id)"
                                     [change]="ruleEntryChange(selectedRule, 'parameterValues', param.id)"
                                     [label]="param.name"
                                     [reviewKey]="'rule:' + selectedRule.id + ':configJson.parameterValues:' + param.id">
                    @if (needsOptions(param)) {
                      <!-- Datentyp „Auswahl": der Wert dieser Regel kommt aus der Optionsliste des
                           Parameters, nicht aus freiem Text. -->
                      <bic-select (valueChange)="setParamValue(param.id, $event)" [label]="param.name"
                                  [value]="getParamValue(param.id)"
                                  placeholder="Auswählen">
                        @for (option of optionsPreview(param); track option) {
                          <bic-dropdown-element [value]="option">{{ option }}</bic-dropdown-element>
                        }
                      </bic-select>
                    } @else {
                      <bic-input (valueChange)="setParamValue(param.id, $event)" [label]="param.name" [showX]="false"
                                 [value]="getParamValue(param.id)"
                                 placeholder=" "></bic-input>
                    }
                  </app-change-marker>
                }
              }
              @for (entry of unlistedRuleEntries(selectedRule, 'parameterValues'); track entry.key) {
                <app-change-marker (acceptClicked)="onRuleEntryAccepted(selectedRule, 'parameterValues', entry.key)"
                                   (rejectClicked)="onRuleEntryRejected(selectedRule, 'parameterValues', entry.key)"
                                   [change]="entry.change"
                                   [label]="entry.name"
                                   [reviewKey]="'rule:' + selectedRule.id + ':configJson.parameterValues:' + entry.key">
                  <bic-input [disabled]="true" [label]="entry.name" [showX]="false" [value]="entry.value"></bic-input>
                </app-change-marker>
              }
              <!-- Auf Set-Ebene gelöscht, Regelwert unverändert: ✓/✗ sind an die Set-Zeile gekoppelt
                   (setRemovedRuleEntries) und gelten damit für alle Prüfregeln. -->
              @for (entry of setRemovedRuleEntries(selectedRule, 'parameterValues'); track entry.key) {
                <app-change-marker (acceptClicked)="acceptRowRemoval('checkParameters', entry.removal)"
                                   (rejectClicked)="rejectRowRemoval('checkParameters', entry.removal)"
                                   [attr.data-proposal-set-removed]="'parameterValues:' + entry.key"
                                   [change]="entry.change"
                                   [label]="entry.name">
                  <bic-input [disabled]="true" [label]="entry.name" [showX]="false" [value]="entry.value"></bic-input>
                  <span class="block pt-1 text-xs text-gray-500">Wird unter „Regelparameter“ für alle Prüfregeln gelöscht – ✓/✗ gelten für alle Prüfregeln.</span>
                </app-change-marker>
              }
            </div>
          </div>
        </div>
        }
      }
    </div>
  </div>
  }
</div>

@if (optionsPopupVisible) {
  <app-options-popup
    (closed)="onOptionsPopupClosed()"
    (saved)="onOptionsPopupSaved($event)"
    [initialOptions]="optionsPopupOptions"
    [label]="optionsPopupLabel">
  </app-options-popup>
}

<!-- File Picker Popup -->
@if (filePickerVisible) {
  <app-context-file-picker
    [excludeIds]="contextFileIds"
    [maxTotal]="MAX_CONTEXT_FILES"
    (selected)="onContextFilesPicked($event)"
    (closed)="closeFilePicker()">
  </app-context-file-picker>
}

@if (promptSuggestionPopupVisible) {
  <bic-popup (close)="closePromptSuggestionPopup()" [customClass]="'w-[700px]'" [hasBackdrop]="true"
             title="Prompt-Vorschlag">
    <div bicPopupContent class="flex flex-col gap-4">
      <span class="text-sm text-gray-500">
        Ein Vorschlag als Ausgangspunkt – am besten anschließend auf die konkrete Prüfregel anpassen.
      </span>
      <div class="flex flex-col gap-2">
        <div class="flex flex-row justify-between items-center">
          <span class="text-sm font-medium text-gray-900">Systemprompt</span>
          <button (click)="applySuggestedSystemprompt()" class="text-xs text-green hover:underline" type="button">
            Übernehmen
          </button>
        </div>
        <div class="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-[180px] overflow-y-auto">{{ suggestedSystemprompt }}</div>
      </div>
      <div class="flex flex-col gap-2">
        <div class="flex flex-row justify-between items-center">
          <span class="text-sm font-medium text-gray-900">Prüfprompt</span>
          <button (click)="applySuggestedPruefprompt()" class="text-xs text-green hover:underline" type="button">
            Übernehmen
          </button>
        </div>
        <div class="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-[180px] overflow-y-auto">{{ suggestedPruefprompt }}</div>
      </div>
    </div>
    <div bicPopupButtonGroup class="flex flex-row w-full justify-end items-center gap-4">
      <bic-button (onClick)="closePromptSuggestionPopup()" buttonType="white">Schließen</bic-button>
    </div>
  </bic-popup>
}

@if (ruleToDelete) {
  <bic-popup (close)="cancelDeleteRule()" [hasBackdrop]="true" customClass="w-[560px]"
             title="Prüfregel löschen?">
    <div bicPopupContent class="flex flex-col gap-4">
      <span class="font-normal">Möchten Sie die Prüfregel „{{ ruleToDelete.name }}" wirklich löschen?<br><br>
        Diese Aktion kann nicht rückgängig gemacht werden.</span>
      <app-rule-usage-warning [loading]="deleteUsagesLoading" [usages]="deleteUsages"></app-rule-usage-warning>
    </div>
    <div bicPopupButtonGroup class="flex flex-row w-full justify-end items-center gap-4">
      <bic-button (onClick)="cancelDeleteRule()" buttonType="white">Abbrechen</bic-button>
      <bic-button (onClick)="confirmDeleteRule()" [disabled]="deleteUsagesLoading" buttonType="primary">Löschen
      </bic-button>
    </div>
  </bic-popup>
}

@if (ruleTestContext) {
  <app-rule-test-popup [context]="ruleTestContext" (closed)="closeRuleTest()"></app-rule-test-popup>
}
